import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ListingCard from '../components/listings/ListingCard';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function UserProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [profileRes, listingsRes] = await Promise.all([
          API.get(`/users/${id}`),
          API.get(`/listings?seller_id=${id}&status=active&limit=12`)
        ]);
        setProfile(profileRes.data);
        setListings(listingsRes.data.listings || []);
        if (currentUser && currentUser.id !== id) {
          API.get(`/users/${id}/follow-status`).then(r => setFollowing(r.data.following)).catch(()=>{});
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
        toast.error('User not found');
        navigate('/browse');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 48px', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }} className="float-anim">🍒</div>
          <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>Loading profile...</div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const { user, reviews } = profile;

  // Star rating
  const renderStars = (rating) => {
    return '⭐'.repeat(Math.floor(rating)) + (rating % 1 >= 0.5 ? '⭐' : '');
  };

  return (
    <div style={{ background: 'var(--cream)', minHeight: 'calc(100vh - 68px)' }}>
      {/* Storefront Banner */}
      {user.seller_banner_url && (
        <div style={{ width: '100%', height: 220, overflow: 'hidden' }}>
          <img src={`${API_BASE}${user.seller_banner_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
      )}

      {/* Header Section */}
      <div style={{ background: 'linear-gradient(135deg, #C0392B 0%, #E74C3C 100%)', color: 'white', padding: '60px 48px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 32, alignItems: 'start' }}>
            {/* Avatar */}
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 48,
                fontWeight: 700,
                overflow: 'hidden',
                border: '3px solid white'
              }}
            >
              {user.avatar_url ? (
                <img src={`${API_BASE}${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                user.first_name?.[0]?.toUpperCase()
              )}
            </div>

            {/* Info */}
            <div>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 36, fontWeight: 700, marginBottom: 8 }}>
                {user.first_name} {user.last_name}
              </h1>

              {/* Rating & Member Since */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 20 }}>{renderStars(parseFloat(user.rating_avg) || 0)}</span>
                  <span style={{ fontSize: 16, fontWeight: 700 }}>
                    {(parseFloat(user.rating_avg) || 0).toFixed(1)} ({user.rating_count} {user.rating_count === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
                <div style={{ fontSize: 14 }}>
                  📅 Member since {user.member_since}
                </div>
              </div>

              {/* Location */}
              {user.location_city && (
                <div style={{ fontSize: 14, marginBottom: 16 }}>
                  📍 {user.location_city}, {user.location_state}
                </div>
              )}

              {/* Response Time */}
              {user.avg_response_hours !== null && (
                <div style={{ fontSize: 14, marginBottom: 16 }}>
                  ⏱ Usually replies in <strong>{Math.round(user.avg_response_hours)} {user.avg_response_hours < 2 ? 'hour' : 'hours'}</strong>
                </div>
              )}

              {/* Follower count */}
              {user.follower_count > 0 && (
                <div style={{ fontSize: 13, marginBottom: 12, opacity: 0.85 }}>
                  👥 {parseInt(user.follower_count).toLocaleString()} follower{user.follower_count !== '1' ? 's' : ''}
                </div>
              )}

              {/* Badges */}
              {user.badges && user.badges.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {user.badges.map((badge, i) => (
                    <div key={i} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 14px', background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.4)', borderRadius:24, fontSize:13, fontWeight:700, backdropFilter:'blur(10px)' }}>
                      <span style={{ fontSize: 16 }}>{badge.icon}</span>
                      {badge.label}
                    </div>
                  ))}
                </div>
              )}

              {/* Follow button */}
              {currentUser && currentUser.id !== id && (
                <button
                  className={`btn ${following ? 'btn-ghost' : 'btn-cherry'}`}
                  style={{ background: following ? 'rgba(255,255,255,0.2)' : 'white', color: following ? 'white' : 'var(--cherry)', border: '1px solid rgba(255,255,255,0.5)' }}
                  onClick={() => API.post(`/users/${id}/follow`).then(r => { setFollowing(r.data.following); toast.success(r.data.following ? 'Following!' : 'Unfollowed'); })}>
                  {following ? '✅ Following' : '+ Follow Seller'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 48px' }}>
        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 20, marginBottom: 60 }}>
          <div style={{ background: 'white', borderRadius: 14, padding: 24, textAlign: 'center', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--cherry)', marginBottom: 8 }}>
              {user.total_listings}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-soft)', fontWeight: 600 }}>Items listed</div>
          </div>
          <div style={{ background: 'white', borderRadius: 14, padding: 24, textAlign: 'center', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#2E7D32', marginBottom: 8 }}>
              {(user.total_sales || 0) + (user.total_rentals || 0)}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-soft)', fontWeight: 600 }}>Successful transactions</div>
          </div>
          <div style={{ background: 'white', borderRadius: 14, padding: 24, textAlign: 'center', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1976D2', marginBottom: 8 }}>
              {parseInt(user.rating_count) || 0}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-soft)', fontWeight: 600 }}>Positive reviews</div>
          </div>
        </div>

        {/* Reviews Section */}
        {reviews && reviews.length > 0 && (
          <div style={{ marginBottom: 60 }}>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 700, marginBottom: 24 }}>
              Reviews ({reviews.length})
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
              {reviews.map(review => (
                <div
                  key={review.id}
                  style={{
                    background: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: 20
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                        {review.first_name} {review.last_name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                        {review.created_at_formatted}
                      </div>
                    </div>
                    <div style={{ fontSize: 18 }}>{renderStars(review.rating)}</div>
                  </div>

                  {review.title && (
                    <div style={{
                      fontWeight: 700,
                      fontSize: 14,
                      marginBottom: 8,
                      color: 'var(--text-dark)'
                    }}>
                      {review.title}
                    </div>
                  )}

                  {review.body && (
                    <div style={{
                      fontSize: 13,
                      color: 'var(--text-mid)',
                      lineHeight: 1.6
                    }}>
                      {review.body}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vacation mode banner */}
        {user.vacation_mode && (
          <div style={{ background:'#FFF9E6', border:'1px solid #F5CBA7', borderRadius:12, padding:'14px 20px', marginBottom:28, display:'flex', gap:12, alignItems:'center' }}>
            <span style={{ fontSize:24 }}>🏖️</span>
            <div>
              <div style={{ fontWeight:700 }}>This seller is on vacation</div>
              {user.vacation_message && <div style={{ fontSize:13, color:'var(--text-mid)', marginTop:2 }}>{user.vacation_message}</div>}
            </div>
          </div>
        )}

        {/* Seller Policies */}
        {(user.return_policy || user.shipping_policy) && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:40 }}>
            {user.shipping_policy && (
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:20 }}>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:8 }}>🚚 Shipping Policy</div>
                <div style={{ fontSize:13, color:'var(--text-mid)', lineHeight:1.6 }}>{user.shipping_policy}</div>
              </div>
            )}
            {user.return_policy && (
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:20 }}>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:8 }}>↩️ Return Policy</div>
                <div style={{ fontSize:13, color:'var(--text-mid)', lineHeight:1.6 }}>{user.return_policy}</div>
              </div>
            )}
          </div>
        )}

        {/* Listings Section */}
        {listings.length > 0 && (
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 700, marginBottom: 24 }}>
              Active Listings ({listings.length})
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 18 }}>
              {listings.map((listing, i) => (
                <ListingCard key={listing.id} listing={listing} delay={i * 0.04} />
              ))}
            </div>
          </div>
        )}

        {listings.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 40px', background: 'white', borderRadius: 14 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏜️</div>
            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
              No active listings
            </h3>
            <p style={{ fontSize: 14, color: 'var(--text-soft)' }}>
              This user doesn't have any active listings at the moment
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
